import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import { Content } from "@/components/layouts/content";
import { leadDashboardQueryOptions } from "@/features/dashboard/api/queries/lead-query";
import type {
	LeadCategoryBreakdown,
	LeadDashboard,
	LeadFollowUpItem,
	LeadMonthlyTrend,
	LeadPipelineItem,
	LeadPriorityItem,
	LeadRecentActivity,
	LeadTopClient,
} from "@/features/dashboard/types/lead";
import { useAuth } from "@/hooks/use-auth";
import { formatNumber } from "@/utils/format-number";
import {
	IconAlertTriangle,
	IconCalendarEvent,
	IconChartBar,
	IconClock,
	IconCurrencyPeso,
	IconTargetArrow,
	IconTrendingUp,
	IconUsers,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

export const Route = createFileRoute("/_dashboard/leads-dashboard")({
	component: LeadsDashboardPage,
});

const PRIORITY_COLORS: Record<string, string> = {
	high: "bg-red-100 text-red-800",
	medium: "bg-yellow-100 text-yellow-800",
	low: "bg-green-100 text-green-800",
	unset: "bg-gray-100 text-gray-500",
};

const STATUS_BAR_COLORS: Record<string, string> = {
	green: "bg-green-500",
	yellow: "bg-yellow-500",
	red: "bg-red-500",
	blue: "bg-blue-500",
	purple: "bg-purple-500",
	gray: "bg-gray-400",
	orange: "bg-orange-500",
};

function formatCurrency(value: number) {
	return new Intl.NumberFormat("en-PH", {
		style: "currency",
		currency: "PHP",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(value);
}

function formatDate(value: string) {
	return new Date(value).toLocaleDateString("en-PH", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function LeadsDashboardPage() {
	const { user } = useAuth();
	const [viewMode, setViewMode] = useState<"my" | "all">("my");
	const userId = viewMode === "my" ? user?.id : undefined;

	const dashboardQuery = useQuery(leadDashboardQueryOptions(userId));
	const data = dashboardQuery.data;

	if (dashboardQuery.isLoading) {
		return (
			<Content pageName="Leads Dashboard">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading dashboard...</p>
					</div>
				</div>
			</Content>
		);
	}

	if (!data) return null;

	return (
		<Content pageName="Leads Dashboard">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				{/* Header */}
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-xl font-semibold text-foreground-dark">Leads Dashboard</h1>
						<p className="text-sm text-foreground/70">Pipeline overview and follow-up tracking</p>
					</div>
					<div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
						<button
							type="button"
							onClick={() => setViewMode("my")}
							className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === "my" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
						>
							My Leads
						</button>
						<button
							type="button"
							onClick={() => setViewMode("all")}
							className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === "all" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
						>
							All Leads
						</button>
					</div>
				</div>

				{/* Summary Cards */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
					<SummaryCard
						icon={<IconTargetArrow className="w-5 h-5" />}
						label="Total Leads"
						value={String(data.summary.total_leads)}
						iconColor="text-blue-600"
						iconBg="bg-blue-50"
					/>
					<SummaryCard
						icon={<IconCurrencyPeso className="w-5 h-5" />}
						label="Pipeline Value"
						value={formatCurrency(data.summary.total_pipeline_value)}
						iconColor="text-green-600"
						iconBg="bg-green-50"
					/>
					<SummaryCard
						icon={<IconClock className="w-5 h-5" />}
						label="Avg Response"
						value={data.summary.avg_response_days != null ? `${data.summary.avg_response_days} days` : "N/A"}
						iconColor="text-purple-600"
						iconBg="bg-purple-50"
					/>
					<SummaryCard
						icon={<IconAlertTriangle className="w-5 h-5" />}
						label="Overdue Follow-ups"
						value={String(data.summary.overdue_follow_ups)}
						iconColor={data.summary.overdue_follow_ups > 0 ? "text-red-600" : "text-gray-500"}
						iconBg={data.summary.overdue_follow_ups > 0 ? "bg-red-50" : "bg-gray-50"}
					/>
				</div>

				{/* Pipeline by Status */}
				<PipelineCard pipeline={data.pipeline} totalValue={data.summary.total_pipeline_value} />

				{/* Follow-ups + Priority */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<FollowUpsCard overdue={data.overdue_follow_ups} upcoming={data.upcoming_follow_ups} />
					<PriorityCard items={data.by_priority} />
				</div>

				{/* Monthly Trend + Top Clients */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<MonthlyTrendCard data={data.monthly_trend} />
					<TopClientsCard clients={data.top_clients} />
				</div>

				{/* Category Breakdown */}
				<CategoryBreakdownCard categories={data.by_category} />

				{/* Recent Activity */}
				<RecentActivityCard activities={data.recent_activity} />
			</div>
		</Content>
	);
}

function SummaryCard({ icon, label, value, iconColor, iconBg }: { icon: React.ReactNode; label: string; value: string; iconColor: string; iconBg: string }) {
	return (
		<Card>
			<CardContent className="p-4">
				<div className="flex items-center gap-3">
					<div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center ${iconColor}`}>{icon}</div>
					<div>
						<p className="text-xs text-gray-500 font-medium">{label}</p>
						<p className="text-lg font-bold text-gray-900">{value}</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function PipelineCard({ pipeline, totalValue }: { pipeline: LeadPipelineItem[]; totalValue: number }) {
	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg flex items-center gap-2">
					<IconChartBar className="w-5 h-5 text-gray-500" />
					Pipeline by Status
				</CardTitle>
			</CardHeader>
			<CardContent>
				{/* Stacked bar */}
				{totalValue > 0 && (
					<div className="flex h-8 rounded-lg overflow-hidden mb-4">
						{pipeline
							.filter((s) => s.total_amount > 0)
							.map((s) => {
								const pct = (s.total_amount / totalValue) * 100;
								return (
									<div
										key={s.slug}
										className={`${STATUS_BAR_COLORS[s.color] || "bg-gray-400"} relative group`}
										style={{ width: `${Math.max(pct, 2)}%` }}
										title={`${s.name}: ${formatCurrency(s.total_amount)} (${pct.toFixed(1)}%)`}
									/>
								);
							})}
					</div>
				)}
				{/* Legend table */}
				<div className="space-y-2">
					{pipeline.map((s) => (
						<div key={s.slug} className="flex items-center justify-between text-sm">
							<div className="flex items-center gap-2">
								<div className={`w-3 h-3 rounded-full ${STATUS_BAR_COLORS[s.color] || "bg-gray-400"}`} />
								<span className="text-gray-700">{s.name}</span>
							</div>
							<div className="flex items-center gap-4">
								<span className="text-gray-500">{s.count} leads</span>
								<span className="font-medium text-gray-900 w-28 text-right">{formatCurrency(s.total_amount)}</span>
							</div>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

function FollowUpsCard({ overdue, upcoming }: { overdue: LeadFollowUpItem[]; upcoming: LeadFollowUpItem[] }) {
	const [tab, setTab] = useState<"overdue" | "upcoming">("overdue");
	const items = tab === "overdue" ? overdue : upcoming;

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle className="text-lg flex items-center gap-2">
						<IconCalendarEvent className="w-5 h-5 text-gray-500" />
						Follow-ups
					</CardTitle>
					<div className="flex gap-1 bg-gray-100 rounded-md p-0.5">
						<button
							type="button"
							onClick={() => setTab("overdue")}
							className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${tab === "overdue" ? "bg-white text-red-700 shadow-sm" : "text-gray-600"}`}
						>
							Overdue ({overdue.length})
						</button>
						<button
							type="button"
							onClick={() => setTab("upcoming")}
							className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${tab === "upcoming" ? "bg-white text-blue-700 shadow-sm" : "text-gray-600"}`}
						>
							Upcoming ({upcoming.length})
						</button>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				{items.length === 0 ? (
					<div className="text-center py-8 text-gray-500">
						<IconCalendarEvent className="w-10 h-10 mx-auto mb-2 text-gray-300" />
						<p className="text-sm">{tab === "overdue" ? "No overdue follow-ups!" : "No upcoming follow-ups this week."}</p>
					</div>
				) : (
					<div className="space-y-3">
						{items.map((item) => (
							<Link
								key={item.id}
								to="/leads/$leadId"
								params={{ leadId: item.id }}
								className="flex items-start justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
							>
								<div className="min-w-0 flex-1">
									<div className="flex items-center gap-2">
										<span className="text-sm font-medium text-blue-600 truncate">{item.reference_number}</span>
										{item.priority && (
											<span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${PRIORITY_COLORS[item.priority]}`}>
												{item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
											</span>
										)}
									</div>
									<p className="text-sm text-gray-900 truncate mt-0.5">{item.client_name}</p>
									<p className="text-xs text-gray-500 truncate">{item.project_description}</p>
								</div>
								<div className="text-right ml-3 shrink-0">
									<p className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</p>
									<p className={`text-xs mt-0.5 ${tab === "overdue" ? "text-red-600 font-medium" : "text-gray-500"}`}>{formatDate(item.next_follow_up_date)}</p>
								</div>
							</Link>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function PriorityCard({ items }: { items: LeadPriorityItem[] }) {
	const total = items.reduce((sum, i) => sum + i.count, 0);
	const order = ["high", "medium", "low", "unset"];
	const sorted = [...items].sort((a, b) => order.indexOf(a.priority) - order.indexOf(b.priority));

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg flex items-center gap-2">
					<IconAlertTriangle className="w-5 h-5 text-gray-500" />
					Leads by Priority
				</CardTitle>
			</CardHeader>
			<CardContent>
				{sorted.length === 0 ? (
					<p className="text-center py-8 text-sm text-gray-500">No leads found.</p>
				) : (
					<div className="space-y-4">
						{sorted.map((item) => {
							const pct = total > 0 ? (item.count / total) * 100 : 0;
							const label = item.priority === "unset" ? "No Priority" : item.priority.charAt(0).toUpperCase() + item.priority.slice(1);
							const barColor =
								item.priority === "high"
									? "bg-red-500"
									: item.priority === "medium"
										? "bg-yellow-500"
										: item.priority === "low"
											? "bg-green-500"
											: "bg-gray-300";
							return (
								<div key={item.priority}>
									<div className="flex items-center justify-between text-sm mb-1">
										<div className="flex items-center gap-2">
											<span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[item.priority]}`}>{label}</span>
										</div>
										<div className="flex items-center gap-3">
											<span className="text-gray-500">{item.count} leads</span>
											<span className="font-medium text-gray-900 w-24 text-right">{formatCurrency(item.total_amount)}</span>
										</div>
									</div>
									<div className="h-2 bg-gray-100 rounded-full overflow-hidden">
										<div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
									</div>
								</div>
							);
						})}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function MonthlyTrendCard({ data }: { data: LeadMonthlyTrend[] }) {
	const maxAmount = Math.max(...data.map((d) => d.total_amount), 1);

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg flex items-center gap-2">
					<IconTrendingUp className="w-5 h-5 text-gray-500" />
					Monthly Trend
				</CardTitle>
			</CardHeader>
			<CardContent>
				{data.length === 0 ? (
					<p className="text-center py-8 text-sm text-gray-500">No data available.</p>
				) : (
					<div className="flex items-end gap-2 h-40">
						{data.map((d) => {
							const heightPct = (d.total_amount / maxAmount) * 100;
							const monthLabel = new Date(`${d.month}-01`).toLocaleDateString("en-PH", { month: "short" });
							return (
								<div key={d.month} className="flex-1 flex flex-col items-center gap-1">
									<span className="text-[10px] text-gray-500 font-medium">{d.count}</span>
									<div className="w-full relative" style={{ height: `${Math.max(heightPct, 4)}%` }}>
										<div
											className="absolute inset-0 bg-blue-500 rounded-t-md hover:bg-blue-600 transition-colors"
											title={`${monthLabel}: ${formatCurrency(d.total_amount)} (${d.count} leads)`}
										/>
									</div>
									<span className="text-xs text-gray-500">{monthLabel}</span>
								</div>
							);
						})}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function TopClientsCard({ clients }: { clients: LeadTopClient[] }) {
	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg flex items-center gap-2">
					<IconUsers className="w-5 h-5 text-gray-500" />
					Top Clients
				</CardTitle>
			</CardHeader>
			<CardContent>
				{clients.length === 0 ? (
					<p className="text-center py-8 text-sm text-gray-500">No clients found.</p>
				) : (
					<div className="space-y-3">
						{clients.map((c, i) => (
							<div key={c.client_name} className="flex items-center justify-between">
								<div className="flex items-center gap-3 min-w-0">
									<span className="text-xs font-bold text-gray-400 w-5 text-right">{i + 1}</span>
									<span className="text-sm text-gray-900 truncate">{c.client_name}</span>
								</div>
								<div className="flex items-center gap-3 shrink-0">
									<span className="text-xs text-gray-500">{c.lead_count} leads</span>
									<span className="text-sm font-medium text-gray-900 w-24 text-right">{formatCurrency(c.total_amount)}</span>
								</div>
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function CategoryBreakdownCard({ categories }: { categories: LeadCategoryBreakdown[] }) {
	const total = categories.reduce((sum, c) => sum + c.total_amount, 0);

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg">Category Breakdown</CardTitle>
			</CardHeader>
			<CardContent>
				{categories.length === 0 ? (
					<p className="text-center py-8 text-sm text-gray-500">No data available.</p>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{categories.map((c) => {
							const pct = total > 0 ? ((c.total_amount / total) * 100).toFixed(1) : "0";
							return (
								<div key={c.category} className="p-3 rounded-lg border border-gray-100 bg-gray-50">
									<p className="text-sm font-medium text-gray-900">{c.category}</p>
									<div className="flex items-center justify-between mt-1">
										<span className="text-xs text-gray-500">
											{c.count} leads ({pct}%)
										</span>
										<span className="text-sm font-semibold text-gray-900">{formatCurrency(c.total_amount)}</span>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function RecentActivityCard({ activities }: { activities: LeadRecentActivity[] }) {
	const eventStyles: Record<string, { color: string; bg: string; icon: string }> = {
		created: { color: "text-green-600", bg: "bg-green-100", icon: "+" },
		updated: { color: "text-blue-600", bg: "bg-blue-100", icon: "~" },
		deleted: { color: "text-red-600", bg: "bg-red-100", icon: "-" },
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg">Recent Activity</CardTitle>
			</CardHeader>
			<CardContent>
				{activities.length === 0 ? (
					<p className="text-center py-8 text-sm text-gray-500">No recent activity.</p>
				) : (
					<div className="relative">
						<div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
						<div className="space-y-4">
							{activities.map((a) => {
								const style = eventStyles[a.event] || eventStyles.updated;
								return (
									<div key={a.id} className="relative pl-10">
										<div className={`absolute left-2 top-1 w-5 h-5 rounded-full ${style.bg} flex items-center justify-center`}>
											<span className={`text-xs font-bold ${style.color}`}>{style.icon}</span>
										</div>
										<div className="flex items-start justify-between">
											<div>
												<span className="text-sm text-gray-900">{a.description}</span>
												{a.lead_reference && (
													<Link to="/leads/$leadId" params={{ leadId: a.lead_id }} className="text-sm text-blue-600 hover:underline ml-1">
														{a.lead_reference}
													</Link>
												)}
												{a.causer && <span className="text-xs text-gray-500 ml-2">by {a.causer.name}</span>}
											</div>
											<span className="text-xs text-gray-400 shrink-0 ml-3">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</span>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
